import type { SupabaseClient } from "@supabase/supabase-js";
import { dbErrorMessage, logDbError } from "../supabase/db-error";
import type { Capture, TranscriptSegment } from "./schema";
import { type CaptureDbRow, mapDbRowToCapture } from "./supabase-mapper";

const BUCKET = "captures-audio";
const SIGNED_URL_TTL_S = 3600; // 1 heure

// Projection « liste » : toutes les colonnes affichées SAUF les lourdes
// (transcript_raw, transcript_segments, summary). Le poll de l'accueil rappelle
// fetchCaptures en boucle ; tirer les transcripts ici = fuite d'egress (mesuré
// 588 ko/appel → quelques ko). `has_transcript` (colonne générée, 1 octet)
// remplace la présence du texte pour le badge d'extraction. Le texte est chargé
// à la demande au dépli via fetchCaptureTranscript.
const LIST_COLUMNS = [
  "id",
  "created_by",
  "project_id",
  "type",
  "title",
  "recorded_at",
  "duration_s",
  "audio_path",
  "keep_audio",
  "status",
  "gist",
  "error_message",
  "meeting_context",
  "batch_extraction",
  "approved_at",
  "approved_by",
  "has_transcript",
  "transcript_edited_at",
  // Anti-perte (I2/C09) : entiers légers, indispensables pour que la pastille
  // « Enregistrement incomplet » (capture-phase.ts) puisse jamais s'allumer —
  // sans eux `elapsedS`/`missingAudioMs` valent toujours `undefined`.
  "elapsed_s",
  "missing_audio_ms",
  "created_at",
  "updated_at",
].join(", ");

export type FetchCapturesResult = { ok: true; data: Capture[] } | { ok: false; error: string };

export type FetchCaptureResult =
  | { ok: true; data: Capture }
  | { ok: false; error: string; notFound?: true };

export type SignedUrlResult = { ok: true; url: string } | { ok: false; error: string };

export type CaptureTranscript = {
  transcriptRaw: string | null;
  transcriptSegments: TranscriptSegment[] | null;
  summary: string | null;
  // Dernière édition manuelle de la transcription (item « Modifier une
  // transcription ») — null si jamais modifiée.
  transcriptEditedAt: string | null;
};

export type FetchTranscriptResult =
  | { ok: true; data: CaptureTranscript }
  | { ok: false; error: string };

// Messages des LECTURES refusées (DS §6.13). « TypeError: Load failed » était
// affiché derrière « Erreur : » sur l'accueil, et « JWT expired » derrière
// « Transcript indisponible : » dans l'onglet Transcription. Les phrases vivent
// ICI : `fetchCaptures` sert l'accueil ET la resynchro, `fetchCaptureById` sert
// la page dédiée. Le geste nomme le bouton « Réessayer » que porte chaque écran
// concerné.
export const CAPTURES_NON_CHARGEES =
  "Tes captures n'ont pas pu être chargées. Vérifie ta connexion, puis touche « Réessayer ».";
export const CAPTURE_NON_CHARGEE =
  "Cette capture n'a pas pu être chargée. Vérifie ta connexion, puis touche « Réessayer ».";
export const TRANSCRIPT_NON_CHARGE =
  "La transcription n'a pas pu être chargée. Vérifie ta connexion, puis touche « Réessayer ».";
// Pas de « réessaie » ici, et c'est délibéré : le cas le plus courant est un
// audio purgé par la rétention (14 jours), et réessayer ne le ramènera pas.
// Le geste utile est de savoir ce qui RESTE.
export const AUDIO_NON_OUVERT =
  "Cet audio n'a pas pu être ouvert — il a peut-être été supprimé après 14 jours. La transcription et les éléments détectés, eux, restent.";

export async function fetchCaptures(client: SupabaseClient): Promise<FetchCapturesResult> {
  const { data, error } = await client
    .from("captures")
    .select(LIST_COLUMNS)
    .order("recorded_at", { ascending: false });

  if (error) {
    return { ok: false, error: dbErrorMessage("captures.fetch", error, CAPTURES_NON_CHARGEES) };
  }

  const rows = (data ?? []) as unknown as CaptureDbRow[];
  const captures: Capture[] = [];
  for (const row of rows) {
    try {
      captures.push(mapDbRowToCapture(row));
    } catch {
      // row corrompue côté DB : on l'ignore plutôt que faire planter la liste
    }
  }
  return { ok: true, data: captures };
}

// Charge UNE capture par id avec la même projection légère que la liste
// (page dédiée /captures/:id) : même contrainte egress que fetchCaptures, on
// ne réutilise donc pas fetchCaptureTranscript ici (colonnes lourdes).
export async function fetchCaptureById(
  client: SupabaseClient,
  captureId: string,
): Promise<FetchCaptureResult> {
  const { data, error } = await client
    .from("captures")
    .select(LIST_COLUMNS)
    .eq("id", captureId)
    .maybeSingle();

  if (error) {
    return { ok: false, error: dbErrorMessage("captures.fetchById", error, CAPTURE_NON_CHARGEE) };
  }
  if (!data) return { ok: false, error: "Capture introuvable", notFound: true };

  try {
    return { ok: true, data: mapDbRowToCapture(data as unknown as CaptureDbRow) };
  } catch (e) {
    // row corrompue côté DB : ne pas laisser l'exception remonter au caller —
    // et surtout ne pas montrer le message de Zod à l'usagère.
    logDbError("captures.fetchById", e instanceof Error ? e.message : String(e));
    return { ok: false, error: CAPTURE_NON_CHARGEE };
  }
}

// Charge le texte du transcript pour UNE capture (au dépli). Séparé de la liste
// pour ne tirer les colonnes lourdes que lorsqu'on en a réellement besoin.
export async function fetchCaptureTranscript(
  client: SupabaseClient,
  captureId: string,
): Promise<FetchTranscriptResult> {
  const { data, error } = await client
    .from("captures")
    .select("transcript_raw, transcript_segments, summary, transcript_edited_at")
    .eq("id", captureId)
    .single();

  if (error || !data) {
    logDbError("captures.fetchTranscript", error?.message ?? "aucune ligne rendue");
    return { ok: false, error: TRANSCRIPT_NON_CHARGE };
  }
  const row = data as Pick<CaptureDbRow, "transcript_raw" | "transcript_segments" | "summary"> & {
    transcript_edited_at?: string | null;
  };
  return {
    ok: true,
    data: {
      transcriptRaw: row.transcript_raw ?? null,
      transcriptSegments: row.transcript_segments ?? null,
      summary: row.summary ?? null,
      transcriptEditedAt: row.transcript_edited_at ?? null,
    },
  };
}

export async function createAudioSignedUrl(
  client: SupabaseClient,
  audioPath: string,
): Promise<SignedUrlResult> {
  const { data, error } = await client.storage
    .from(BUCKET)
    .createSignedUrl(audioPath, SIGNED_URL_TTL_S);
  if (error || !data) {
    logDbError("captures.audioSignedUrl", error?.message ?? "aucune URL rendue");
    return { ok: false, error: AUDIO_NON_OUVERT };
  }
  return { ok: true, url: data.signedUrl };
}
